export const createEndpoint = (template, params = {}) => {
  let endpoint = template;

  // Replace URL parameters (:param)
  Object.entries(params).forEach(([key, value]) => {
    endpoint = endpoint.replace(`:${key}`, value);
  });

  // Replace query parameters ({{param}})
  Object.entries(params).forEach(([key, value]) => {
    const placeholder = `{{${key}}}`;
    if (endpoint.includes(placeholder)) {
      endpoint = endpoint.replace(placeholder, encodeURIComponent(value));
    }
  });

  return endpoint;
};

export const API_ENDPOINTS = {
  AUTH: {
    VERIFY: '/auth/verify',
    REFRESH: '/ncandidates/auth/refresh-token',
  },
  USER: {
    GOOGLEAUTH: '/ncandidates/auth/google/signin/initiate',
    GOOGLEAUTHSIGNUP: '/candidates/google/url?cid={{cid}}',
    GOOGLEAUTHCALLBACK:
      '/candidates/google/callback?code={{code}}&state={{state}}',
    REGISTER: '/candidates/register-candidate',
    LOGIN: '/ncandidates/auth/login',
    LOGOUT: '/ncandidates/auth/logout',
    PROFILE: '/user/profile',
    UPDATE: '/user/update',
    ACTIVATE: '/auth/verify-email/:token',
    SENDOTP: '/candidates/request-email-otp',
    VERIFYOTP: '/candidates/verify-email-otp',
    RESENDOTP: '/candidates/resend-email-otp',
    // Email Linking OTP endpoints (for secondary auth)
    LINK_EMAIL_SENDOTP: '/ncandidates/auth/email/request-otp',
    LINK_EMAIL_VERIFYOTP: '/ncandidates/auth/secondary/email/link',
    LINK_EMAIL_RESENDOTP: '/ncandidates/auth/email/resend-otp',
    LINK_EMAIL_SUBMIT: '/candidates/auth/link-email/submit',
    GOOGLE_SECONDARY_CONFIG: '/ncandidates/auth/secondary/google/config',
    GOOGLE_SECONDARY_INITIATE: '/ncandidates/auth/secondary/google/initiate',
    GOOGLE_SECONDARY_LINK: '/ncandidates/auth/secondary/google/link',
    // Phone Login OTP endpoints
    LOGIN_PHONE_SENDOTP: '/ncandidates/auth/phone/request-otp',
    LOGIN_PHONE_VERIFYOTP: '/ncandidates/auth/verify-otp',
    LOGIN_PHONE_RESENDOTP: '/ncandidates/auth/phone/resend-otp',
    FORGOTPASSWORD: '/candidates/auth/forgot-password',
    RESETPASSWORD: '/candidates/auth/reset-password',
    CHANGEPASSWORD: '/candidates/auth/change-password',
    ACKNOWLEDGE_INTRO: '/candidates/acknowledge-intro',
  },
  PROFILE_EMAIL: {
    REQUEST_OTP: '/ncandidates/profile/email/request-otp',
    RESEND_OTP: '/ncandidates/profile/email/resend-otp',
    VERIFY_CHANGE: '/ncandidates/profile/email/verify-change',
  },
  RESUME: {
    SIGNUP_SENDOTP: '/ncandidates/auth/signup/initiate',
    SIGNUP_VERIFYOTP: '/ncandidates/auth/verify-otp',
    SIGNUP_RESENDOTP: '/ncandidates/auth/phone/resend-otp',
    REQUESTSIGNEDURL: '/candidates/generate-upload-url',
    FORMSUBMISSION: '/candidates/submit-basic-profile',
    GETPROFILE: '/candidates/profile',
    GETISPROCESSED: '/candidates/processed',
    UPDATEPROFILE: '/candidates/completeprofile',
    // Public profile-link endpoints (backend updated)
    UPDATEPROFILE_PUBLIC: '/candidates/profile-link/',
    UPDATEPROFILE_SECTION: '/candidates/profile',
    // Public fetch by token (backend updated)
    RESUMEPROFILE: '/candidates/profile-link/:token',
    GETSUMMARY: '/candidates/profile/summary',
    GETREFERENCEDATA: '/candidates/reference-data',
    // Server-generated resume PDF (binary; opened in a new tab).
    RESUME_PDF: 'candidates/profile-highlights-pdf',
    // Resolve city + state from a 6-digit PIN code.
    PINCODE_LOOKUP: '/candidates/pincode/:pincode',
    GET_BASIC_INFO: '/candidates/basic-info/:token',
    GET_UPLOAD_PROCESSING: '/candidates/profile/upload-processing',
    // Public profile-link endpoints
    GETREFERENCEDATA_PUBLIC: '/candidates/profile-link/reference-data',
    GETPROFILE_PUBLIC: '/candidates/profile-link', // No trailing slash to prevent 307 redirect
    UPDATEPROFILE_PUBLIC_LINK: '/candidates/profile-link', // No trailing slash to prevent 307 redirect
    ENHANCE_WORK_EXPERIENCE_RESPONSIBILITY:
      '/candidates/profile/work-experience/enhance-responsibility',
    // Work-experience CRUD (separate from the section-PATCH used by other
    // sections). Create+edit WEs in one POST (new rows embed duties, existing
    // rows send id + changed fields only); delete by id. Duties of an existing
    // WE are managed via their own nested endpoints.
    WORK_EXPERIENCE: '/candidates/profile/work-experience',
    WORK_EXPERIENCE_BY_ID: '/candidates/profile/work-experience/:id',
    WORK_EXPERIENCE_DUTIES: '/candidates/profile/work-experience/:expId/duties',
    WORK_EXPERIENCE_DUTY_BY_ID:
      '/candidates/profile/work-experience/:expId/duties/:dutyId',
    // Same work-experience CRUD, token-authenticated for the public
    // profile-link (magic link) route — token passed as `?token=`.
    WORK_EXPERIENCE_PUBLIC: '/candidates/profile-link/work-experience',
    WORK_EXPERIENCE_PUBLIC_BY_ID:
      '/candidates/profile-link/work-experience/:id',
    WORK_EXPERIENCE_DUTIES_PUBLIC:
      '/candidates/profile-link/work-experience/:expId/duties',
    WORK_EXPERIENCE_DUTY_BY_ID_PUBLIC:
      '/candidates/profile-link/work-experience/:expId/duties/:dutyId',
  },
  // Senior Level Flow — candidate-facing, token-authenticated (PR #748 §3).
  // Mirrors MY_PIPELINES below one-for-one, under /public/, with the link
  // token passed as a `?token=` query param on every call. `:sec_mapping_id`
  // is decoded client-side from that token's JWT payload (see
  // SeniorFlowService's secMappingIdFromToken) since there's no bearer
  // session here to scope the request with.
  SENIOR_FLOW: {
    LANDING: '/candidates/public/my-pipelines/:sec_mapping_id/landing',
    ACKNOWLEDGE:
      '/candidates/public/my-pipelines/:sec_mapping_id/acknowledge-contact',
    PROFILE: '/candidates/public/my-pipelines/:sec_mapping_id/profile',
    // GET returns the full requirement list with candidate_* pre-fill fields;
    // PATCH sends only the skills the candidate confirmed having, keyed by
    // name (not id) — see SkillsStage.jsx's toSkillMatchPayload.
    SKILL_MATCH: '/candidates/public/my-pipelines/:sec_mapping_id/skill-match',
    // Accept/decline the interview scheduled *within this same flow*
    // (separate contract from INTERVIEW_INVITE's acknowledge below) — no
    // sec_mapping_id-scoped mirror yet.
    ACCEPT_INTERVIEW: '/candidates/candidates-pipeline/accept-interview',
    DECLINE_INTERVIEW: '/candidates/candidates-pipeline/decline-interview',
  },
  // Interview invite — candidate-facing, token-authenticated. `:sec_mapping_id`
  // is carried inside the signed link JWT (decoded client-side to build the
  // URL); the token is also passed as `?token=` and re-verified by the backend.
  INTERVIEW_INVITE: {
    INFO: '/candidates/public/my-pipelines/:sec_mapping_id/interview-invite',
    // Candidate confirms / declines attendance — body { is_accepted: bool }.
    ACKNOWLEDGE:
      '/candidates/public/my-pipelines/:sec_mapping_id/acknowledge-interview',
  },
  // My Applications (protected, bearer-auth) — the listing endpoints, plus
  // per-item detail/action endpoints that mirror the magic-link SENIOR_FLOW /
  // INTERVIEW_INVITE contracts above (same payload shapes), scoped by
  // `:sec_mapping_id` instead of a signed `?token=`.
  MY_PIPELINES: {
    INTEREST_REQUESTS: '/candidates/my-pipelines/interest-requests',
    INTERVIEWS: '/candidates/my-pipelines/interviews',
    LANDING: '/candidates/my-pipelines/:sec_mapping_id/landing',
    ACKNOWLEDGE: '/candidates/my-pipelines/:sec_mapping_id/acknowledge-contact',
    PROFILE: '/candidates/my-pipelines/:sec_mapping_id/profile',
    INTERVIEW_INVITE:
      '/candidates/my-pipelines/:sec_mapping_id/interview-invite',
    ACKNOWLEDGE_INTERVIEW:
      '/candidates/my-pipelines/:sec_mapping_id/acknowledge-interview',
    SKILL_MATCH: '/candidates/my-pipelines/:sec_mapping_id/skill-match',
  },
  // Add more endpoints as needed
};
